import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { Select } from "@/components/ui/select"

export interface SubNavItem {
  key: string
  label: string
  /** Route-based tabs navigate on click; omit for state-based tabs driven by onChange. */
  href?: string
}

type SubNavProps = {
  items: SubNavItem[]
  active: string
  onChange?: (key: string) => void
  /** Optional right-side slot — secondary links, filters, small actions. */
  right?: React.ReactNode
  ariaLabel?: string
}

/**
 * Design-system "Sub navigation" pattern: a horizontal tab strip on md+,
 * collapsing to a full-width dropdown below md. Tabs either link to sibling
 * routes (href) or switch in-page panels (onChange).
 */
export function SubNav({ items, active, onChange, right, ariaLabel = "Section" }: SubNavProps) {
  const select = (key: string) => {
    const item = items.find((i) => i.key === key)
    if (!item) return
    if (item.href) router.visit(item.href)
    else onChange?.(key)
  }

  return (
    <>
      {/* Mobile (< md): full-width dropdown showing the active tab */}
      <div className="border-b border-hairline md:hidden">
        {right && <div className="flex items-center gap-3 pt-2">{right}</div>}
        <div className="py-2">
          <Select
            aria-label={ariaLabel}
            value={active}
            onChange={(e) => select(e.target.value)}
            className="w-full"
          >
            {items.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Desktop (md+): horizontal tab strip */}
      <nav
        aria-label={ariaLabel}
        className="hidden items-end justify-between gap-4 border-b border-hairline md:flex"
      >
        <div className="flex items-center gap-6">
          {items.map((item) => {
            const isActive = item.key === active
            const className = cn(
              "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm no-underline",
              isActive
                ? "border-accent font-medium text-accent-display"
                : "border-transparent text-ink-body hover:text-ink-display",
            )
            return item.href ? (
              <Link key={item.key} href={item.href} className={className}>
                {item.label}
              </Link>
            ) : (
              <button key={item.key} type="button" className={className} onClick={() => onChange?.(item.key)}>
                {item.label}
              </button>
            )
          })}
        </div>
        {right && <div className="flex items-center gap-3 pb-2">{right}</div>}
      </nav>
    </>
  )
}
